import { Input } from "antd";
import type { InputProps } from "antd";
import React, { useEffect, useRef, useState } from "react";

import {
  formatGermanAmount,
  isAllowedAmountText,
  parseGermanAmount,
} from "@/modules/amount-input/parseGermanAmount";

/*
 * Why this exists (SMT-1358): antd's `InputNumber` re-runs its `formatter` on
 * every keystroke and then re-parses that formatted output, so a formatter
 * that adds thousands separators fights with what the operator is currently
 * typing - "33,33" turned into "30,03,33", "140" into "1,00". A plain text
 * field has no such formatter, so it can hold exactly what was typed and
 * format only once, on blur.
 *
 * This component keeps a leading minus and hands it to `parseGermanAmount`
 * as a signed value, rather than silently stripping it. That is deliberate:
 * the Backoffice-wide `numberParser` (`src/v1/components/common/helpers.tsx`)
 * strips the sign before any validation rule sees the value, which is why the
 * FsE tax-registration fields used to carry their own `parseSignedAmount`
 * (SMT-1295) to keep it - now replaced by this component, which never uses
 * `numberParser`. `numberParser` itself stays untouched - other fields still
 * depend on its behaviour.
 *
 * On blur, the display is rebuilt from the `value` prop rather than from the
 * locally typed text whenever `value` is defined: normally the two agree (the
 * prop is just the last thing `onChange` emitted), but if the form changed
 * `value` from outside while this field had focus - e.g. `setFieldsValue` -
 * blur is what reconciles the display with that external change instead of
 * discarding it. Only a field with no `value` at all (uncontrolled) falls
 * back to parsing the typed text.
 *
 * The sync effect below also has to tell an external change apart from our
 * own typing while focused: every keystroke's `onChange` normally comes back
 * around as a new `value` prop (that is how a controlled field works), and
 * reformatting the display on that round trip would fight the operator the
 * same way `InputNumber` did. So while focused, the effect compares the
 * incoming `value` against what the current text itself already parses to -
 * equal means it is our own keystroke coming back, different means the value
 * changed out from under the operator (e.g. an OCR prefill landing on an
 * autoFocused, still-empty field), which must be shown immediately rather
 * than only once the field is left (SMT-1358 CodeRabbit follow-up).
 */

export type AmountInputProps = Omit<InputProps, "value" | "onChange"> & {
  value?: number | null;
  onChange?: (value: number | null) => void;
  precision?: number;
  allowNegative?: boolean;
  useGrouping?: boolean;
};

export const AmountInput: React.FC<AmountInputProps> = ({
  value,
  onChange,
  precision = 2,
  allowNegative = false,
  useGrouping = true,
  onBlur,
  onFocus,
  ...inputProps
}) => {
  const isFocused = useRef(false);

  const [text, setText] = useState<string>(() =>
    value === null || value === undefined
      ? ""
      : formatGermanAmount(value, { precision, useGrouping })
  );

  // Sync the display from the `value` prop when it changes from outside
  // (form load/reset, or a genuinely external change while focused - see the
  // file comment above) - but not when it is simply our own last keystroke
  // coming back around as a prop, which would overwrite half-typed input
  // with a reformatted value.
  useEffect(() => {
    const normalizedValue = value ?? null;

    if (isFocused.current) {
      const parsedText = parseGermanAmount(text, { precision });
      if (normalizedValue === parsedText) {
        return;
      }
    }

    setText(
      normalizedValue === null
        ? ""
        : formatGermanAmount(normalizedValue, { precision, useGrouping })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, precision, useGrouping]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // A paste can carry a copied amount's spaces and "€", which typing alone
    // never produces. Strip them before the filter sees the text, or a
    // perfectly good pasted amount like "1.234,56 €" is rejected wholesale.
    const nextText = event.target.value.replace(/\s/g, "").replace(/€/g, "");

    if (!isAllowedAmountText(nextText, { precision, allowNegative })) {
      return;
    }

    // Keep the text exactly as typed - never reformat while typing.
    setText(nextText);
    onChange?.(parseGermanAmount(nextText, { precision }));
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    isFocused.current = true;
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    isFocused.current = false;

    // Prefer the `value` prop over the locally typed text: it already holds
    // what the last `onChange` emitted, unless something external changed it
    // in the meantime (e.g. a `setFieldsValue` while this field had focus,
    // which the effect above deliberately did not apply to `text`). Only an
    // uncontrolled field (no `value` at all) falls back to parsing `text`.
    const parsed =
      value !== undefined ? value : parseGermanAmount(text, { precision });
    setText(
      parsed === null
        ? ""
        : formatGermanAmount(parsed, { precision, useGrouping })
    );

    onBlur?.(event);
  };

  return (
    <Input
      {...inputProps}
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};
